import React, { useState, useMemo } from "react";
import CountUpWrapper from "../../CountUpWrapper";

const ECO_LEVELS = [
  { label: "New Member",    emoji: "⭐", min: 0,    max: 0,    color: "#B0BABF", nextLabel: "Eco Starter",  nextTarget: 1 },
  { label: "Eco Starter",   emoji: "🌱", min: 1,    max: 49,   color: "#65BC6A", nextLabel: "Bronze Saver", nextTarget: 50 },
  { label: "Bronze Saver",  emoji: "🥉", min: 50,   max: 199,  color: "#CD7F32", nextLabel: "Silver Saver", nextTarget: 200 },
  { label: "Silver Saver",  emoji: "🥈", min: 200,  max: 499,  color: "#9E9E9E", nextLabel: "Gold Saver",   nextTarget: 500 },
  { label: "Gold Saver",    emoji: "🥇", min: 500,  max: 999,  color: "#FFD700", nextLabel: "Platinum",     nextTarget: 1000 },
  { label: "Platinum",      emoji: "💎", min: 1000, max: Infinity, color: "#7B2FBE", nextLabel: null, nextTarget: null },
];

function getEcoLevel(points) {
  for (let i = ECO_LEVELS.length - 1; i >= 0; i--) {
    if (points >= ECO_LEVELS[i].min) return ECO_LEVELS[i];
  }
  return ECO_LEVELS[0];
}

function getProgressPercent(points, level) {
  if (!level.nextTarget) return 100;
  const range = level.nextTarget - level.min;
  const progress = points - level.min;
  return Math.min(100, Math.round((progress / range) * 100));
}

const UserProfile = ({ user }) => {
  const [showEcoInfo, setShowEcoInfo] = useState(false);
  const [showMessagesPanel, setShowMessagesPanel] = useState(false);

  const totalPoints = useMemo(
    () => (user?.recyclingHistory || []).reduce((sum, e) => sum + (e.pointsEarned || 0), 0),
    [user]
  );

  const ecoLevel = getEcoLevel(totalPoints);
  const progressPercent = getProgressPercent(totalPoints, ecoLevel);

  const pendingMessages = useMemo(
    () => (user?.recyclingHistory || []).filter((e) => e.status === "Processing" || e.status === "Pending"),
    [user]
  );

  const totalItems = (user?.recyclingHistory || []).reduce((sum, e) => sum + (e.itemsRecycled || 1), 0);

  return (
    <div className="bg-white font-Inter p-8 rounded-3xl shadow-md w-full max-w-lg mx-auto overflow-visible" data-aos="fade-in" data-aos-delay="100">
      {/* Eco Level Progress Bar */}
      <div data-aos="fade-down" data-aos-delay="200" className="mx-auto relative w-full mb-6">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-400">Level progress</span>
          <span className="text-xs font-medium" style={{ color: ecoLevel.color }}>
            {ecoLevel.emoji} {ecoLevel.label}
            {ecoLevel.nextLabel && (
              <span className="text-gray-400 font-normal"> → {ecoLevel.nextLabel}</span>
            )}
          </span>
        </div>
        <div className="h-2 w-full bg-[#EEFAF7] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${progressPercent}%`, backgroundColor: ecoLevel.color }}
          ></div>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-400">{totalPoints} pts</span>
          {ecoLevel.nextTarget && (
            <span className="text-xs text-gray-400">{ecoLevel.nextTarget} pts</span>
          )}
        </div>
      </div>

      {/* User Image + Name + More Options */}
      <div className="flex items-center mb-6 relative">
        <div className="w-20 h-20 rounded-full overflow-hidden mr-4 ring-4 ring-[#EEFAF7] flex-shrink-0" data-aos="fade-right" data-aos-delay="400">
          {user?.profilePicture ? (
            <img src={user.profilePicture} alt="User" className="w-full h-full object-cover" />
          ) : (
            <div className="bg-[#A7D3BF] w-full h-full flex items-center justify-center text-[#18421B] text-3xl font-bold">
              {user?.username ? user.username.charAt(0).toUpperCase() : "?"}
            </div>
          )}
        </div>

        <div data-aos="fade-right" data-aos-delay="400" className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-[#414042] truncate">{user?.username || "Loading..."}</h2>
          <p className="text-sm text-[#6C757D] truncate">{user?.email || "Loading..."}</p>
          <span className="inline-flex items-center mt-1 text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: ecoLevel.color + "22", color: ecoLevel.color }}>
            {ecoLevel.emoji} {ecoLevel.label}
          </span>
        </div>

        {/* Settings shortcut */}
        <a
          href="/settings"
          title="Account settings"
          data-aos="fade-left"
          data-aos-delay="500"
          className="absolute top-0 right-0 w-8 h-8 rounded-full bg-[#EEFAF7] flex items-center justify-center text-[#29752B] hover:bg-[#004d3f] hover:text-white transition-all"
        >
          <i className="fas fa-cog text-sm"></i>
        </a>
      </div>

      {/* Address and Phone */}
      <div className="grid grid-cols-1 gap-3 mb-6">
        <div className="flex items-start">
          <span className="bg-[#18421B] text-white text-xs font-medium py-1 px-2 rounded-md mr-2 mt-0.5 flex-shrink-0" data-aos="fade-right" data-aos-delay="300">Address</span>
          <p className="text-[#414042] text-sm" data-aos="fade-in" data-aos-delay="300">{user?.address || "Loading..."}</p>
        </div>
        <div className="flex items-center">
          <span className="bg-[#18421B] text-white text-xs font-medium py-1 px-2 rounded-md mr-2 flex-shrink-0" data-aos="fade-right" data-aos-delay="400">Phone</span>
          <p className="text-[#414042] text-sm" data-aos="fade-in" data-aos-delay="400">{user?.mobileNumber || "Loading..."}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Items Recycled */}
        <div className="flex items-center bg-[#7DD1E4]/10 rounded-xl p-3" data-aos="fade-in" data-aos-delay="400">
          <img src="/images/Recycle_bin.png" alt="Recycled items" className="h-10 w-10 mr-2 flex-shrink-0" />
          <div>
            <span className="text-2xl font-bold text-[#172A3A]">
              <CountUpWrapper start={0} end={totalItems} duration={2} redraw={true} />
            </span>
            <p className="text-xs text-gray-400">Items recycled</p>
          </div>
        </div>

        {/* Environmental impact */}
        <div className="flex items-center bg-[#7DD1E4]/10 rounded-xl p-3" data-aos="fade-in" data-aos-delay="500">
          <img src="/images/world-map-icon2.png" alt="Impact" className="h-10 w-10 mr-2 flex-shrink-0" />
          <div>
            <span className="text-2xl font-bold text-[#172A3A]">
              <CountUpWrapper start={0} end={(user?.recyclingHistory || []).reduce((sum, e) => sum + (typeof e.environmentalImpact === 'number' ? e.environmentalImpact : 0), 0)} duration={2} redraw={true} />
            </span>
            <p className="text-xs text-gray-400">Env. impact</p>
          </div>
        </div>
      </div>

      {/* Eco Saver + Messages Buttons */}
      <div className="grid grid-cols-2 gap-3">
        {/* Eco Saver */}
        <div className="relative">
          <button
            onClick={() => setShowEcoInfo(!showEcoInfo)}
            className="w-full bg-white text-[#344054] px-3 py-2 rounded-xl flex items-center justify-between border border-gray-200 hover:bg-gray-50 transition text-sm"
            data-aos="fade-in" data-aos-delay="600"
          >
            <span className="font-medium">Eco Saver</span>
            <span className="text-xs px-1.5 py-0.5 rounded-full font-bold text-white" style={{ backgroundColor: ecoLevel.color }}>
              {ecoLevel.emoji}
            </span>
          </button>

          {showEcoInfo && (
            <div className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 z-50">
              <h4 className="font-bold text-sm text-[#172A3A] mb-3">Eco Saver Levels</h4>
              {ECO_LEVELS.map((lvl) => (
                <div key={lvl.label} className={`flex items-center py-1.5 px-2 rounded-lg mb-1 ${lvl.label === ecoLevel.label ? "bg-[#EEFAF7]" : ""}`}>
                  <span className="text-base mr-2">{lvl.emoji}</span>
                  <div className="flex-1">
                    <span className="text-xs font-medium text-[#344054]">{lvl.label}</span>
                    <span className="text-xs text-gray-400 ml-1">
                      {lvl.max === Infinity ? `${lvl.min}+ pts` : lvl.min === 0 && lvl.max === 0 ? "0 pts" : `${lvl.min}–${lvl.max} pts`}
                    </span>
                  </div>
                  {lvl.label === ecoLevel.label && (
                    <span className="text-xs text-[#29752B] font-medium">You</span>
                  )}
                </div>
              ))}
              <button onClick={() => setShowEcoInfo(false)} className="mt-2 text-xs text-gray-400 hover:text-gray-600">Close</button>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="relative">
          <button
            onClick={() => setShowMessagesPanel(!showMessagesPanel)}
            className="w-full bg-white text-[#344054] px-3 py-2 rounded-xl flex items-center justify-between border border-gray-200 hover:bg-gray-50 transition text-sm"
            data-aos="fade-in" data-aos-delay="700"
          >
            <span className="font-medium">Updates</span>
            {pendingMessages.length > 0 ? (
              <span className="bg-[#FF6666] text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                {pendingMessages.length}
              </span>
            ) : (
              <span className="bg-gray-200 text-gray-500 text-xs px-1.5 py-0.5 rounded-full">0</span>
            )}
          </button>

          {showMessagesPanel && (
            <div className="absolute bottom-full right-0 mb-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 z-50">
              <h4 className="font-bold text-sm text-[#172A3A] mb-3">Pending Updates</h4>
              {pendingMessages.length === 0 ? (
                <p className="text-xs text-gray-400 italic">All clear — no pending requests.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {pendingMessages.map((msg, i) => (
                    <div key={i} className="flex items-start p-2 bg-gray-50 rounded-xl">
                      <span className={`w-2 h-2 rounded-full mt-1.5 mr-2 flex-shrink-0 ${msg.status === "Processing" ? "bg-yellow-400" : "bg-blue-400"}`}></span>
                      <div>
                        <p className="text-xs font-medium text-[#344054]">{msg.deviceType}</p>
                        <p className="text-xs text-gray-400">{msg.status} · {new Date(msg.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setShowMessagesPanel(false)} className="mt-3 text-xs text-gray-400 hover:text-gray-600">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
